// SCR-CONSOLE-020 (افزوده AP-INT-09؛ US-INT-108): مدیریت کاتالوگ نسخه‌دار نگاشت خطای بیمه‌گر و شمارنده خطاهای
// نگاشت‌نشده.
//
// کاتالوگ **حذف ندارد** (مثل ماتریس انطباق): ردیف منتشرشده فقط ویرایش می‌شود. `isRetryable` تنها برای دسته `Transient`
// مجاز است (BR-INT-111) و متن نمونه خطاهای نگاشت‌نشده پاک‌سازی و کوتاه شده است (BR-INT-030).
import DoneAllOutlinedIcon from '@mui/icons-material/DoneAllOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { unwrap, type InsurerErrorMapRow, type UnmappedInsurerErrorRow } from '@phoenix/api-client';
import { usePlatformSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, ErrorState, faDigits, FormDialog, JalaliDate, StatusChip, type DataTableColumn } from '@phoenix/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

/** دسته‌های مجاز کاتالوگ نگاشت (BR-INT-111). */
const CATEGORIES = ['Credential', 'Patient', 'Data', 'Duplicate', 'Rule', 'Transient', 'Unmapped'] as const;

const INSURERS = ['TAMIN', 'SALAMAT', 'ARMED'] as const;

const keys = {
  map: (insurer: string) => ['integration', 'console', 'error-map', insurer] as const,
  unmapped: (insurer: string) => ['integration', 'console', 'unmapped-errors', insurer] as const,
};

interface MapForm {
  key: string;
  insurer: string;
  matchText: string;
  category: string;
  code: string;
  messageKey: string;
  isRetryable: boolean;
  sortOrder: number;
}

const emptyForm = (insurer: string): MapForm => ({
  key: '',
  insurer,
  matchText: '',
  category: 'Data',
  code: 'PHX-INT-0319',
  messageKey: 'error.integration_provider_message',
  isRetryable: false,
  sortOrder: 100,
});

/** کاتالوگ نگاشت خطای بیمه‌گر و شمارنده نگاشت‌نشده‌ها (AC-INT-108.3). */
export function InsurerErrorMapSection() {
  const { t } = useTranslation('int');
  const { api } = usePlatformSession();
  const queryClient = useQueryClient();
  const [insurer, setInsurer] = useState<string>('TAMIN');
  const [form, setForm] = useState<MapForm | null>(null);

  const rows = useQuery({
    queryKey: keys.map(insurer),
    queryFn: () =>
      unwrap(api.GET('/api/v1/integration/platform/insurer-error-map', { params: { query: { insurer } } })),
  });

  const unmapped = useQuery({
    queryKey: keys.unmapped(insurer),
    queryFn: () =>
      unwrap(
        api.GET('/api/v1/integration/platform/unmapped-insurer-errors', {
          params: { query: { insurer, includeResolved: false } },
        }),
      ),
  });

  const upsert = useMutation({
    mutationFn: (value: MapForm) =>
      unwrap(
        api.PUT('/api/v1/integration/platform/insurer-error-map/{key}', {
          params: { path: { key: value.key } },
          body: {
            insurer: value.insurer,
            matchText: value.matchText.trim() === '' ? null : value.matchText.trim(),
            matchHttpFrom: null,
            matchHttpTo: null,
            category: value.category,
            code: value.code,
            messageKey: value.messageKey,
            isRetryable: value.isRetryable,
            sortOrder: Number(value.sortOrder),
          },
        }),
      ),
    onSuccess: () => {
      setForm(null);
      void queryClient.invalidateQueries({ queryKey: keys.map(insurer) });
    },
  });

  const resolve = useMutation({
    mutationFn: (id: string) =>
      unwrap(
        api.POST('/api/v1/integration/platform/unmapped-insurer-errors/{id}:resolve', {
          params: { path: { id } },
        }),
      ),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: keys.unmapped(insurer) }),
  });

  const columns: DataTableColumn<InsurerErrorMapRow>[] = [
    { id: 'key', headerKey: 'int:error_map.columns.key', cell: (row) => <bdi dir="ltr">{row.key}</bdi> },
    { id: 'match', headerKey: 'int:error_map.columns.match', cell: (row) => row.matchText ?? t('int:error_map.any') },
    {
      id: 'category',
      headerKey: 'int:error_map.columns.category',
      cell: (row) => t(`int:error_map.category.${row.category}`, { defaultValue: row.category }),
    },
    { id: 'code', headerKey: 'int:error_map.columns.code', cell: (row) => <bdi dir="ltr">{row.code}</bdi> },
    {
      id: 'retryable',
      headerKey: 'int:error_map.columns.retryable',
      cell: (row) => (
        <StatusChip
          tone={row.isRetryable ? 'pending' : 'inactive'}
          labelKey={row.isRetryable ? 'int:error_map.retryable' : 'int:error_map.final'}
        />
      ),
    },
    { id: 'version', headerKey: 'int:error_map.columns.version', cell: (row) => faDigits(Number(row.rowVersion)) },
    {
      id: 'actions',
      headerKey: 'common:actions',
      cell: (row) => (
        <Tooltip title={t('int:error_map.edit')}>
          <IconButton
            size="small"
            onClick={() =>
              setForm({
                key: row.key,
                insurer: row.insurer,
                matchText: row.matchText ?? '',
                category: row.category,
                code: row.code,
                messageKey: row.messageKey,
                isRetryable: row.isRetryable,
                sortOrder: Number(row.sortOrder),
              })
            }
            data-testid={`insurer-error-map-edit-${row.key}`}
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  const unmappedColumns: DataTableColumn<UnmappedInsurerErrorRow>[] = [
    { id: 'sample', headerKey: 'int:error_map.unmapped.sample', cell: (row) => row.sample },
    { id: 'occurrences', headerKey: 'int:error_map.unmapped.occurrences', cell: (row) => faDigits(Number(row.occurrences)) },
    {
      id: 'lastSeen',
      headerKey: 'int:error_map.unmapped.last_seen',
      cell: (row) => <JalaliDate value={row.lastSeenAt} withTime />,
    },
    {
      id: 'actions',
      headerKey: 'common:actions',
      cell: (row) => (
        <Tooltip title={t('int:error_map.unmapped.resolve')}>
          <IconButton
            size="small"
            onClick={() => resolve.mutate(row.id)}
            disabled={resolve.isPending}
            data-testid={`unmapped-insurer-error-resolve-${row.id}`}
          >
            <DoneAllOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  return (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="insurer-error-map-section">
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="subtitle1">{t('int:error_map.title')}</Typography>
          <TextField
            select
            size="small"
            label={t('int:error_map.insurer')}
            value={insurer}
            onChange={(event) => setInsurer(event.target.value)}
            sx={{ minWidth: 160 }}
          >
            {INSURERS.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`int:insurer_credentials.insurer.${value}`, { defaultValue: value })}
              </MenuItem>
            ))}
          </TextField>
          <Button size="small" variant="contained" onClick={() => setForm(emptyForm(insurer))} data-testid="insurer-error-map-add">
            {t('int:error_map.add')}
          </Button>
        </Stack>

        <Typography variant="body2" color="text.secondary">
          {t('int:error_map.no_delete')}
        </Typography>

        {rows.error ? <ErrorState error={rows.error} compact onRetry={() => void rows.refetch()} /> : null}
        <div data-testid="insurer-error-map-table">
          <DataTable
            columns={columns}
            rows={(rows.data?.items ?? []) as InsurerErrorMapRow[]}
            getRowId={(row) => row.key}
            captionKey="int:error_map.title"
            isLoading={rows.isPending}
            emptyTitleKey="int:error_map.empty"
          />
        </div>

        <Typography variant="subtitle2">{t('int:error_map.unmapped.title')}</Typography>
        <div data-testid="unmapped-insurer-errors-table">
          <DataTable
            columns={unmappedColumns}
            rows={(unmapped.data?.items ?? []) as UnmappedInsurerErrorRow[]}
            getRowId={(row) => row.id}
            captionKey="int:error_map.unmapped.title"
            isLoading={unmapped.isPending}
            emptyTitleKey="int:error_map.unmapped.empty"
          />
        </div>
      </Stack>

      <FormDialog
        open={form !== null}
        titleKey="int:error_map.form_title"
        submitKey="common:actions.save"
        isDirty={form !== null}
        isSubmitting={upsert.isPending}
        submitError={upsert.error}
        onClose={() => setForm(null)}
        onSubmit={() => {
          if (form) {
            upsert.mutate(form);
          }
        }}
      >
        {form ? (
          <Stack spacing={2} data-testid="insurer-error-map-form">
            <TextField
              size="small"
              label={t('int:error_map.columns.key')}
              value={form.key}
              onChange={(event) => setForm({ ...form, key: event.target.value })}
              slotProps={{ htmlInput: { 'data-testid': 'insurer-error-map-key', dir: 'ltr' } }}
            />
            <TextField
              size="small"
              label={t('int:error_map.columns.match')}
              value={form.matchText}
              onChange={(event) => setForm({ ...form, matchText: event.target.value })}
              slotProps={{ htmlInput: { 'data-testid': 'insurer-error-map-match' } }}
            />
            <TextField
              select
              size="small"
              label={t('int:error_map.columns.category')}
              value={form.category}
              onChange={(event) => setForm({ ...form, category: event.target.value })}
            >
              {CATEGORIES.map((value) => (
                <MenuItem key={value} value={value}>
                  {t(`int:error_map.category.${value}`, { defaultValue: value })}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              label={t('int:error_map.columns.code')}
              value={form.code}
              onChange={(event) => setForm({ ...form, code: event.target.value })}
              slotProps={{ htmlInput: { 'data-testid': 'insurer-error-map-code', dir: 'ltr' } }}
            />
            <TextField
              size="small"
              label={t('int:error_map.message_key')}
              value={form.messageKey}
              onChange={(event) => setForm({ ...form, messageKey: event.target.value })}
              slotProps={{ htmlInput: { 'data-testid': 'insurer-error-map-message-key', dir: 'ltr' } }}
            />
            <TextField
              select
              size="small"
              label={t('int:error_map.columns.retryable')}
              value={form.isRetryable ? 'yes' : 'no'}
              onChange={(event) => setForm({ ...form, isRetryable: event.target.value === 'yes' })}
              helperText={t('int:error_map.retryable_hint')}
            >
              <MenuItem value="no">{t('int:error_map.final')}</MenuItem>
              <MenuItem value="yes">{t('int:error_map.retryable')}</MenuItem>
            </TextField>
            <TextField
              size="small"
              type="number"
              label={t('int:error_map.columns.order')}
              value={form.sortOrder}
              onChange={(event) => setForm({ ...form, sortOrder: Number(event.target.value) })}
              slotProps={{ htmlInput: { 'data-testid': 'insurer-error-map-order' } }}
            />
          </Stack>
        ) : null}
      </FormDialog>
    </Paper>
  );
}
